import { formatDateKey, formatDateTimeAt } from '@/shared/formatters/date';
import type {
  AtoDaCitacao,
  ResumoDaCitacaoDto,
  SituacaoDaCitacao,
  VarianteSituacaoCitacao,
} from '@/types/arbitral/citacaoRequerido';

import { rotuloMeioCitacao } from './rotulosCitacaoRequerido';
import { capitalizarPrimeiraLetra, rotularEnum } from './text';

/*
 * Os textos e as cores da situação da citação, literais da mensagem do Jonathan (D-04, 29/10). Uma
 * fonte só para o certificado (tela), a Secretaria, o árbitro e a página pública; o core porta este
 * arquivo para o PDF (`certificadoAcessos/templates/documento/visao/situacaoDaCitacao.ts`) e a
 * paridade é conferida por fixture (`pdfDoServidorParidade.test.ts` guarda o hash deste arquivo).
 * Peça de prova: acento, vírgula e travessão fazem parte do texto.
 */

const SITUACAO: Record<SituacaoDaCitacao, { rotulo: string; variante: VarianteSituacaoCitacao }> = {
  SEM_COMUNICACAO: { rotulo: 'Sem comunicação enviada', variante: 'neutral' },
  EM_ENVIO: { rotulo: 'Em envio', variante: 'neutral' },
  ENVIADO_SEM_ENTREGA: { rotulo: 'Enviado, entrega não confirmada', variante: 'warning' },
  ENTREGUE_SEM_CITACAO: { rotulo: 'Entregue, sem citação', variante: 'warning' },
  CITADO: { rotulo: 'Citado', variante: 'success' },
  AGUARDANDO_AR: { rotulo: 'Aguardando AR', variante: 'warning' },
  IDENTIDADE_NEGADA: { rotulo: 'Identidade negada', variante: 'error' },
  SEM_WHATSAPP_VALIDO: { rotulo: 'Sem WhatsApp válido', variante: 'error' },
  NENHUM_CANAL_DIGITAL: { rotulo: 'Nenhum canal digital chegou', variante: 'error' },
};

/** A linha do ato abaixo do "Citado": o ato e, entre parênteses, a origem. */
const LINHA_DO_ATO: Record<Exclude<AtoDaCitacao, 'REGISTRO_SECRETARIA'>, string> = {
  CLIQUE_EMAIL: 'Clicou no link do e-mail (Infobip)',
  TOQUE_RCS: 'Tocou em Acessar o processo (Pontaltech)',
  LINK_PORTAL: 'Entrou no processo pelo link',
  CONFIRMOU_NA_CONVERSA: 'Confirmou na conversa (Lexi)',
  CONFIRMOU_COM_DUVIDAS: 'Confirmou na conversa, com dúvidas (Lexi)',
  MANIFESTACAO_PORTAL: 'Protocolou manifestação no portal',
  ENTREGA_CLAUSULA: 'Por entrega, cláusula contratual',
};

/**
 * O ato no evento ("Citado — {ato}"), sem a origem entre parênteses, como no exemplo da mensagem
 * ("Citado — Clicou no link do e-mail"). D-01 (d) do plano: valida o Jonathan no PR.
 */
const ATO_NO_EVENTO: Record<Exclude<AtoDaCitacao, 'REGISTRO_SECRETARIA'>, string> = {
  CLIQUE_EMAIL: 'Clicou no link do e-mail',
  TOQUE_RCS: 'Tocou em Acessar o processo',
  LINK_PORTAL: 'Entrou no processo pelo link',
  CONFIRMOU_NA_CONVERSA: 'Confirmou na conversa',
  CONFIRMOU_COM_DUVIDAS: 'Confirmou na conversa, com dúvidas',
  MANIFESTACAO_PORTAL: 'Protocolou manifestação no portal',
  ENTREGA_CLAUSULA: 'Por entrega, cláusula contratual',
};

/** Valor que o front ainda não conhece vira o enum humanizado, nunca some. */
function humanizado(valor: string): string {
  return capitalizarPrimeiraLetra(rotularEnum(valor));
}

function ehSituacaoConhecida(valor: string): valor is SituacaoDaCitacao {
  return Object.hasOwn(SITUACAO, valor);
}

function ehAtoComTextoFixo(valor: string): valor is Exclude<AtoDaCitacao, 'REGISTRO_SECRETARIA'> {
  return Object.hasOwn(LINHA_DO_ATO, valor);
}

/** @example rotuloSituacaoCitacao('AGUARDANDO_AR') // 'Aguardando AR' */
export function rotuloSituacaoCitacao(situacao: string): string {
  return ehSituacaoConhecida(situacao) ? SITUACAO[situacao].rotulo : humanizado(situacao);
}

/** Situação desconhecida fica cinza: o selo não afirma citação nem falha que não conhece. */
export function varianteSituacaoCitacao(situacao: string): VarianteSituacaoCitacao {
  return ehSituacaoConhecida(situacao) ? SITUACAO[situacao].variante : 'neutral';
}

/**
 * A data do "Citado": o instante do ato, em Brasília, ou o dia que a Secretaria declarou no
 * registro (dia civil, sem passar por fuso). Sem registro do evento, nada — nunca se inventa data.
 *
 * @example dataDaCitacao({ situacao: 'CITADO', citadoEm: '2026-10-29T18:46:00.000Z', ... }) // '29/10/2026 às 15:46'
 */
export function dataDaCitacao(resumo: ResumoDaCitacaoDto): string | null {
  if (resumo.situacao !== 'CITADO') return null;
  if (resumo.citadoEm) return formatDateTimeAt(resumo.citadoEm) || null;
  if (resumo.citadoNoDia) return formatDateKey(resumo.citadoNoDia) || null;
  return null;
}

/**
 * O selo com a data, no formato que o certificado da sentença já usa ("rótulo · data"): "Citado ·
 * 29/10/2026 às 15:46". D-01 (a) do plano: valida o Jonathan no PR.
 */
export function seloDaCitacao(resumo: ResumoDaCitacaoDto): {
  label: string;
  variante: VarianteSituacaoCitacao;
} {
  const data = dataDaCitacao(resumo);
  const rotulo = rotuloSituacaoCitacao(resumo.situacao);
  return {
    label: data ? `${rotulo} · ${data}` : rotulo,
    variante: varianteSituacaoCitacao(resumo.situacao),
  };
}

/**
 * A linha do ato abaixo do "Citado". No registro, o meio e, quando o resumo o traz, quem
 * registrou ("Registrado pela Secretaria: E-mail, por Maria"; D-01 (b) do plano). O core decide
 * onde o nome vai (D-05): o painel do árbitro nem o recebe.
 *
 * @example linhaDoAtoDaCitacao({ situacao: 'CITADO', ato: 'CLIQUE_EMAIL', ... }) // 'Clicou no link do e-mail (Infobip)'
 */
export function linhaDoAtoDaCitacao(resumo: ResumoDaCitacaoDto): string | null {
  if (resumo.situacao !== 'CITADO' || !resumo.ato) return null;
  if (resumo.ato === 'REGISTRO_SECRETARIA') {
    const meio = resumo.meioRegistrado ? `: ${rotuloMeioCitacao(resumo.meioRegistrado)}` : '';
    const quem = resumo.registradoPor ? `, por ${resumo.registradoPor}` : '';
    return `Registrado pela Secretaria${meio}${quem}`;
  }
  return ehAtoComTextoFixo(resumo.ato) ? LINHA_DO_ATO[resumo.ato] : humanizado(resumo.ato);
}

/**
 * O ato no evento da citação, sem o "Citado — " e sem o instante, que quem lista acrescenta. No
 * registro, o meio e o dia do ato: "Registrado pela Secretaria: E-mail (ato em 29/10/2026)".
 */
export function atoNoEventoDaCitacao(evento: {
  ato: string | null;
  meio: string | null;
  dataAto: string | null;
}): string | null {
  if (!evento.ato) return null;
  if (evento.ato === 'REGISTRO_SECRETARIA') {
    const meio = evento.meio ? `: ${rotuloMeioCitacao(evento.meio)}` : '';
    const dia = evento.dataAto ? ` (ato em ${formatDateKey(evento.dataAto)})` : '';
    return `Registrado pela Secretaria${meio}${dia}`;
  }
  return ehAtoComTextoFixo(evento.ato) ? ATO_NO_EVENTO[evento.ato] : humanizado(evento.ato);
}
